import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Container, Stack, styled, TextField, Typography } from '@mui/material';

import { BotMessage } from 'components/Chat/BotMessage';

import { useApi } from '../../hooks/useApi';

const Wrapper = styled(Stack)`
  position: relative;
`;

const UserMessage = styled('div')`
  max-width: 600px;
  width: fit-content;
  border-radius: 12px;
  margin: 0 0 0 auto;
  background-color: ${({ theme }) => theme.palette.primary.main};
  padding: 12px 16px;
  color: #fff;
  white-space: break-spaces;
`;

interface IChatProps {
  isNewChat: boolean;
  setIsNewChat: (isNewChat: boolean) => void;
  id: string | null;
  setActiveChatId: (id: null) => void;
  agentId: string;
}

export const Chat = ({ isNewChat, setActiveChatId, setIsNewChat, id, agentId }: IChatProps) => {
  const { apiFetch } = useApi();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [prevMessage, setPrevMessage] = useState('');
  const textBlock = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessages([]);
    setMessage('');
    setIsNewChat(false);
    setChatId(null);
  }, [isNewChat]);

  useEffect(() => {
    if (id) {
      setChatId(id);

      (async () => {
        const res = await apiFetch(`chat/${id}/messages`);
        const data = await res.json();

        setMessages(data);
      })();
    }
  }, [id]);

  const sendMessage = async () => {
    const trimedMessage = message.trim();
    if (!trimedMessage && !prevMessage) return;

    const isPrevError = !!messages.at(-1)?.error;

    if (isPrevError) {
      const newMessage = [...messages];

      setMessages(newMessage.slice(0, -1));
    } else {
      setMessages((prev) => [
        ...prev,
        {
          rol: 'user',
          text: trimedMessage,
          id: Date.now().toString(),
        },
      ]);

      setPrevMessage(trimedMessage);
    }

    setLoading(true);

    try {
      const res = await apiFetch('chat/message', {
        method: 'POST',
        body: JSON.stringify({
          chatId,
          agentId,
          message: trimedMessage || prevMessage,
        }),
      });

      setMessage('');

      const data = await res.json();

      setActiveChatId(data.id);

      setMessages((prev) => [...prev, { ...data, rol: 'bot' }]);

      if (!data.error) {
        setPrevMessage('');
      }

      setChatId(data.chatId);
    } catch (e) {
      console.error('Send message failed', e);
    } finally {
      setLoading(false);
    }
  };

  const textBlockHeight = textBlock.current?.clientHeight ?? 100;

  const maxHeight = `calc(75vh - ${textBlockHeight}px - 40px)`;

  useLayoutEffect(() => {
    containerRef.current?.lastElementChild?.scrollIntoView({
      block: 'end',
      behavior: 'smooth',
    });
  }, [messages]);

  return (
    <Container maxWidth="md">
      <Stack
        sx={{
          height: '75vh',
          flex: 1,
        }}
        justifyContent="space-between"
      >
        <Stack
          ref={containerRef}
          sx={{
            height: maxHeight,
            overflow: 'auto',
            pr: 1.5,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: (theme) => theme.palette.neutral[100],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {messages.length ? (
            <Wrapper>
              {messages?.map((mess) => (
                <Fragment key={mess.id || mess.chatId}>
                  {mess.rol === 'user' ? (
                    <UserMessage>
                      <Typography variant="body1">{mess.text}</Typography>
                    </UserMessage>
                  ) : (
                    <BotMessage
                      message={mess}
                      refetch={sendMessage}
                    />
                  )}
                </Fragment>
              ))}
              {loading && <BotMessage loading={loading} />}
            </Wrapper>
          ) : (
            <Stack
              sx={{
                margin: 'auto 0',
              }}
            >
              <Typography variant="h4">Hello there!</Typography>
              <Typography
                variant="h5"
                sx={{
                  color: (theme) => theme.palette.neutral[500],
                  fontWeight: 400,
                  marginTop: 2,
                }}
              >
                How can I help you today?
              </Typography>
            </Stack>
          )}
        </Stack>
        <Wrapper ref={textBlock}>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              sendMessage();
            }}
            sx={{
              position: 'absolute',
              right: '10px',
              bottom: '20px',
              zIndex: 2,
            }}
          >
            <svg
              focusable="false"
              aria-hidden="true"
              viewBox="0 0 24 24"
              style={{
                fill: 'white',
              }}
            >
              <path d="m4 12 1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8z"></path>
            </svg>
          </Button>
          <TextField
            fullWidth
            placeholder="Send a message..."
            value={loading ? '' : message}
            onChange={(e) => setMessage(e.target.value)}
            variant="outlined"
            multiline
            minRows={3}
            maxRows={3}
            onSubmit={sendMessage}
            sx={{
              height: 'auto',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
                sendMessage();
              } else if (e.key === 'Enter' && e.ctrlKey) {
                setMessage((prev) => prev + '\n');
              }
            }}
          />
        </Wrapper>
      </Stack>
    </Container>
  );
};
